"use client";

import { useState } from "react";
import { useAuth } from "@/lib/auth/context";
import { useSundayPlan } from "@/lib/hooks/useSundayPlan";
import { isInPlanWindow } from "@/lib/utils/ritualWindows";
import { SundayPlanSheet } from "./SundayPlanSheet";

type Props = {
  /** Bardziej zwarty layout (bez chevronu, mniejsze padding) — gdy stoi
      obok innego banneru w gridzie 2-kolumnowym. */
  compact?: boolean;
};

/**
 * Banner niedzielnego planowania — widoczny w oknie nd 00:00 → pn 09:00.
 * Zachęca do otwarcia sheetu "Zaplanujmy tydzień razem".
 * Brak streaków, brak nacisku — gdy okno mija, banner sam znika.
 */
export function SundayPlanBanner({ compact = false }: Props = {}) {
  const { userDoc } = useAuth();
  const hid = userDoc?.householdId;
  const plan = useSundayPlan(hid);
  const [open, setOpen] = useState(false);

  if (!isInPlanWindow()) return null;
  if (plan.loading) return null;

  const proposalsCount = plan.proposals.length;
  const subtitle =
    proposalsCount > 0
      ? `${proposalsCount} ${proposalsLabel(proposalsCount)} od dzieci`
      : "Ułóżcie razem tydzień";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          compact
            ? "w-full flex items-center gap-2 rounded-[16px] border border-line px-3 py-2 text-left transition-[transform] active:translate-y-px"
            : "w-full flex items-center gap-3 rounded-[16px] border border-line px-3.5 py-2.5 text-left transition-[transform] active:translate-y-px"
        }
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(58,114,69,0.12), rgba(58,114,69,0.04))",
        }}
      >
        <div className={compact ? "text-[18px] leading-none shrink-0" : "text-[22px] leading-none shrink-0"}>📅</div>
        <div className="flex-1 min-w-0">
          <div className={compact ? "text-[12px] font-bold text-navy leading-tight truncate" : "text-[13px] font-bold text-navy leading-tight"}>
            {compact ? "Plan tygodnia" : "Zaplanuj tydzień razem"}
          </div>
          <div className={compact ? "text-[10px] text-muted truncate" : "text-[11px] text-muted mt-0.5 truncate"}>
            {subtitle}
          </div>
        </div>
        {compact ? null : <div className="text-muted text-lg leading-none shrink-0">›</div>}
      </button>
      <SundayPlanSheet open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function proposalsLabel(n: number): string {
  if (n === 1) return "propozycję";
  // 2-4 / 22-24 itp. → "propozycje", inaczej → "propozycji"
  const last = n % 10;
  const lastTwo = n % 100;
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return "propozycje";
  return "propozycji";
}
